// ══════════════════════════════════════════════════════════════════
//  부품 상점 화면 — **표현층.** 규칙은 `gearshop.js` 에 있고 여기는 규칙을 모른다.
//
//  ■ **레이아웃 【사장님 그림 2026-07-29】**
//      ┌────────┐  ┌──────────────────────────────┐
//      │        │  │  현재 장착 4파츠 (가로 한 줄)   │
//      │ 다윗   │  └──────────────────────────────┘
//      │        │  ┌─────┐┌─────┐┌─────┐┌─────┐
//      └────────┘  │상품1││상품2││상품3││상품4│
//                  └─────┘└─────┘└─────┘└─────┘
//    **장착 칸과 상품 칸이 x축을 공유한다** — 같은 부위가 위아래로 선다.
//    그래서 오버 시 조명만 켜면 시선이 이어진다(선을 안 긋는다).
//
//  ■ **급(▮)과 등급(●)은 다른 기호다.**
//      급   = 얼마나 단단히 무나 — **모든 부품에 상시.** 돌은 「이만큼 무겁다」,
//             나머지는 「이만큼까지 받친다」. 낮은 급도 장착되며 붉은 막대가 손실을 말한다
//      등급 = 얼마나 좋나 — 점 5개 + 색(흰/파랑/주황/보라/빨강)
//
//  ■ **오버 = 지금 낀 것과의 비교.** 삼각형과 색은 모두 **성능 방향**을 말한다.
//    이득은 언제나 `초록 ▲`, 손해는 언제나 `빨강 ▼`다. 숫자의 실제 증감은
//    `현재값 → 장착 후 값`으로 따로 보여 주므로 `빨강 ▲` 같은 충돌이 생기지 않는다.
//
//  ■ **아트는 공란.** 부위마다 빈 자리만 둔다. 텍스처 키 `gear-{부위}-{부품id}`.
//
//  ■ 회색박스 규율: **자리표시자는 자리표시자로 보이게 둔다.**
// ══════════════════════════════════════════════════════════════════
//  ■ **막대(F) 【사장님 지시】** — 막대는 **절대값**이고 **항상 보인다.**
//      막대        절대값 · 항상 보인다     무게(4칸 공통) + 부위별 2줄
//      마우스오버   증감 · 삼각형과 색       **지금 것 그대로 유지**
//    ⚠ 둘은 **다른 것을 말한다.** 막대는 「지금 얼마인가」, 삼각형은 「바꾸면 얼마나
//      달라지나」다. 삼각형·초록/빨강을 지우지 않는다 — 오버할 때 그대로 뜬다.
//    ⚠ **무게 막대는 네 칸에 다 있고 x 가 같다.** 장착 줄과 상품 줄이 x축을 공유하므로
//      (위 그림) 같은 x·같은 자로 그리면 **여덟 칸이 세로로 한 줄에 선다.**
//      *"뭐랑 뭐가 짝꿍인지 우리만 알았던"* 문제가 닫히는 자리가 그것이다 —
//      전에는 장착 줄이 `tx+3`, 상품 줄이 `cx+3+TIER_MAX*17+14` 로 **x 가 달라서**
//      같은 눈금인데도 나란히 놓고 볼 수가 없었다.
import Phaser from 'phaser';
import { EQUIP_FX } from './config.js';
// ⚠ 구 import 의 `CLS_KO` 를 뺐다 — **이 파일 어디서도 안 쓰고 있었다**(급 이름은
//   `gearshop.face()` 가 카드 문구로 쓴다). 죽은 배선을 남기지 않는다(규율 5).
import { SLOTS, SLOT_KO, TIER_MAX, TIER_DIAMOND, isDiamondTier, PARTS, BASE_SHARDS,
         resolveGear, setOf, looseSlots } from './gear.js';
// 아트 매니페스트는 **Phaser 없는 모듈**에 있다 — 노드(배포 복사·치수 검사)도 같은 표를
// 읽어야 하기 때문이다. 키 규약(`gearArtKey`)도 거기로 옮겼다(전에 이 파일에 있었다).
import { GEAR_ART, gearArtKey } from './gearart.js';
import {
  combatRuleCopy,
  combatSnapshot,
  performanceRows,
  setRequirementStatus,
} from './gearstats.js';
import {
  TIER_COLOR, TIER_CSS, tierColor, tierCss,
  drawTierDiamond as drawDiamond, drawTierDots as drawDots,
} from './gearhud.js';
import { candidateLeadChange } from './presentation.js';
export { TIER_COLOR, TIER_CSS } from './gearhud.js';

const W = 1920, H = 1080;
export const CANDIDATE_DETAIL_ROW_GAP = 19;

const GOOD = '#91b578', BAD = '#c96f5d', DIM = '#928d83', INK = '#e8e1d4';

// ── 자·간격을 한 곳에서 잡는다. **전부 여기서만 나온다** ───────────
const PAD = 40, GAP = 14, ROW = 28;
// 필드와 다른 고해상도 인물 컷아웃이 상점의 1/3을 차지하던 판을 폐기했다. 왼쪽은
// 필드와 같은 64px 파수꾼을 큰 픽셀로 보여 주는 장비 작업대이고, 오른쪽 비교 칸이
// 실제 화면의 주인공이다.
const LEFT_W = 360;
const RIGHT_X = PAD + LEFT_W + ROW;
const RIGHT_W = W - RIGHT_X - PAD;
const COL_W = Math.floor((RIGHT_W - GAP * 3) / 4);
export const CANDIDATE_LEAD_MAX_W = COL_W - 28;
const TOP_Y = 138, TOP_H = 208;
const BOT_Y = TOP_Y + TOP_H + 24;
const BOT_H = 548;
const WORN_PAD_X = 14, WORN_GAP = 12, WORN_TOP = 50, WORN_BOTTOM = 14;
const WORN_W = Math.floor((RIGHT_W - WORN_PAD_X * 2 - WORN_GAP * 3) / 4);

export const GSKIN = {
  depth: 200,
  dim: { color: 0x080a0b, alpha: 0.97 },
  r: 0,
  panel: { fill: 0x141719, stroke: 0x596064 },
  // 카드 몸은 전용 직물 PNG가 맡고, 이 색은 상태를 읽히게 하는 얇은 워시만 맡는다.
  fill:   { idle: 0x000000, bought: 0x203128, poor: 0x000000 },
  strokeW: { idle: 3, hover: 5, bought: 4, poor: 3 },
  icon: { w: 96, h: 96, fill: 0x080a0b, stroke: 0x554d43 },
  // 「챙겨두기」는 카드 오른쪽 빈 여백의 작은 자물쇠 하나로만 말한다.
  // 장착 가능 여부와는 무관하며, 실제 `shop.isLocked(i)`가 참일 때만 보인다.
  keepLock: { right: 48, y: 70, size: 64 },
  hover: { washAlpha: 0.16 },
  // ── 폰트: **네 단으로 통일한다.** 그 밖의 크기를 쓰지 않는다 ──
  font: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif',
  fs: { title: '38px', name: '27px', body: '19px', label: '16px' },
};

// 세트는 등급색과 다른 정보다. 카드 테두리·타이틀은 등급색을 그대로 지키고,
// 장착 패널의 얇은 호흡만 수식어별 무광색을 쓴다. 이름·효과 문구는 setOf()가
// SET_KO/SET_FX_KO 정본에서 넘긴 값을 그대로 표시한다.
export const SET_PANEL_COLOR = {
  dry: 0xc8b98f, loose: 0x7ca7b5, rough: 0x6fa697,
  tamed: 0xb87558, aged: 0x8a7ca7, hard: 0xa26962,
};
export const SET_PANEL_CSS = {
  dry: '#c8b98f', loose: '#7ca7b5', rough: '#6fa697',
  tamed: '#b87558', aged: '#8a7ca7', hard: '#a26962',
};
export const SET_PANEL_FX_SEC = 0.92;
// ◆◆◆◆는 일반 세트보다 한 박자 더 쓴다. 그래도 강화창의 손을 묶지 않도록
// 1.2초 안에서 「모임 → 결속 → 국소 폭죽」 세 구절을 모두 끝낸다.
export const DIAMOND_SET_PANEL_FX_SEC = 1.12;

export function shopBoxes() {
  return SLOTS.map((_, i) => ({ x: RIGHT_X + i * (COL_W + GAP), y: BOT_Y, w: COL_W, h: BOT_H }));
}
export function wornPanelBox() {
  return { x: RIGHT_X, y: TOP_Y, w: RIGHT_W, h: TOP_H };
}
export function wornBoxes() {
  return SLOTS.map((_, i) => ({
    x: RIGHT_X + WORN_PAD_X + i * (WORN_W + WORN_GAP),
    y: TOP_Y + WORN_TOP,
    w: WORN_W,
    h: TOP_H - WORN_TOP - WORN_BOTTOM,
  }));
}
export function performanceBox() {
  return { x: PAD + 20, y: TOP_Y + 414, w: LEFT_W - 40, h: 346 };
}
export function rulesButtonBox() {
  // 회색박스의 개발자 설정 접이가 오른쪽 위에 겹쳐도 실제 규칙 버튼을 가리지 않는다.
  // 여정 화면에도 같은 좌표를 써서 두 실행 경로의 상점 배치를 하나로 유지한다.
  return { x: W - 390, y: 46, w: 168, h: 44 };
}
export function rulesPanelBox() {
  return { x: 260, y: 140, w: 1400, h: 800 };
}
export function rulesCloseBox() {
  const p = rulesPanelBox();
  return { x: p.x + p.w - 180, y: p.y + 24, w: 140, h: 42 };
}

// ── 막대 자리 — **여덟 칸이 이 한 자를 쓴다** ─────────────────────
// x 를 칸의 **오른쪽 끝에서** 잡는다. 장착 칸(왼쪽에 아이콘 96)과 상품 칸(아이콘 가운데)의
// 왼쪽 여백이 서로 다르므로, 오른쪽 정렬만이 **두 줄의 x 를 일치시키는** 방법이다.
const BAR_W = 76;
const barX = (b) => b.x + b.w - 14 - BAR_W;

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));
const easeOut = (v) => 1 - (1 - clamp01(v)) ** 3;

// 네 장착 카드가 한 벌로 잠기는 ◆ 전용 문장. 모든 좌표는 wornPanelBox() 안에 있고,
// 화면·상품 카드에는 한 픽셀도 내보내지 않는다. 무작위 입자를 쓰지 않아 같은 구매는
// 언제나 같은 박자로 읽히며, 프레임마다 객체를 만들지 않고 Graphics 한 장만 재사용한다.
function drawDiamondSetCompletion(g, panel, fx, setColor) {
  if (!fx) return;
  const cards = wornBoxes();
  const k = clamp01(fx.k);
  const gather = easeOut(k / 0.42);
  const gatherFade = 1 - clamp01((k - 0.48) / 0.18);
  const bind = easeOut((k - 0.24) / 0.30);
  const burst = clamp01((k - 0.52) / 0.48);
  const burstEase = easeOut(burst);
  const burstFade = Math.sin(Math.PI * burst);
  const cx = panel.x + panel.w / 2;
  const cy = cards[0].y + cards[0].h / 2;

  // 장착 카드 열의 네 모서리에서 중앙 결속점으로 선과 네모 불꽃이 모인다.
  const corners = [
    [cards[0].x + 4, cards[0].y + 4],
    [cards[3].x + cards[3].w - 4, cards[3].y + 4],
    [cards[0].x + 4, cards[0].y + cards[0].h - 4],
    [cards[3].x + cards[3].w - 4, cards[3].y + cards[3].h - 4],
  ];
  if (gatherFade > 0) {
    g.lineStyle(2.2, 0xd9fbff, (0.28 + 0.62 * gather) * gatherFade);
    for (let i = 0; i < corners.length; i++) {
      const [x0, y0] = corners[i];
      const x1 = x0 + (cx - x0) * gather;
      const y1 = y0 + (cy - y0) * gather;
      g.lineBetween(x0, y0, x1, y1);
      const px = Math.round(x1) - 2, py = Math.round(y1) - 2;
      g.fillStyle(i % 2 ? 0xd9fbff : setColor, 0.48 + 0.46 * gather);
      g.fillRect(px, py, 4, 4);
    }

    // 네 카드의 중심도 뒤이어 한 점으로 묶인다. 텍스트보다 아래에서 흐르므로 수치는
    // 그대로 읽히고, 네 부품이 모두 참여했다는 사실만 얇은 선으로 남는다.
    const inner = clamp01((gather - 0.38) / 0.62);
    g.lineStyle(1.4, setColor, 0.20 + inner * 0.46);
    for (const card of cards) {
      const x0 = card.x + card.w / 2, y0 = card.y + card.h / 2;
      g.lineBetween(x0, y0, x0 + (cx - x0) * inner, y0 + (cy - y0) * inner);
    }
  }

  // 네 선이 만난 뒤에만 ◆가 생긴다. 흰 핵과 수식어색 받침을 분리해 밝기를 올리지
  // 않고도 5성의 단순 맥동보다 단단하게 읽힌다.
  if (bind > 0) {
    g.fillStyle(setColor, 0.12 + 0.16 * bind);
    g.fillCircle(cx, cy, 13 + 11 * bind);
    drawDiamond(g, cx, cy, 0.52 + 0.68 * bind, 0.42 + 0.58 * bind);
    g.lineStyle(1.2 + bind * 1.8, 0xd9fbff, 0.28 + 0.48 * bind);
    g.strokeCircle(cx, cy, 15 + bind * 13);
  }

  // 결속 뒤의 폭죽은 패널 반경 안에서만 타원형으로 터진다. 작은 픽셀 12개와 짧은
  // 꼬리뿐이라 4카드의 정보층을 덮지 않고, 끝점도 패널 inset을 절대 넘지 않는다.
  if (burst > 0 && burst < 1) {
    const rx = 34 + 212 * burstEase;
    const ry = 14 + 66 * burstEase;
    for (let i = 0; i < 12; i++) {
      const a = -Math.PI + (i / 12) * Math.PI * 2;
      const x1 = cx + Math.cos(a) * rx;
      const y1 = cy + Math.sin(a) * ry;
      const tail = 7 + 13 * burstFade;
      const ux = Math.cos(a), uy = Math.sin(a);
      const color = i % 3 === 0 ? 0xf4ffff : setColor;
      g.lineStyle(i % 3 === 0 ? 2 : 1.2, color, 0.20 + 0.62 * burstFade);
      g.lineBetween(x1 - ux * tail, y1 - uy * tail, x1, y1);
      const size = i % 3 === 0 ? 5 : 3;
      g.fillStyle(color, 0.34 + 0.60 * burstFade);
      g.fillRect(Math.round(x1 - size / 2), Math.round(y1 - size / 2), size, size);
    }
  }
}

// 장착 패널 안에서만 보이는 세트 표식. 화면 전체·상품 카드·등급선에는 손대지 않는다.
// 여섯 수식어는 같은 패널 문법을 공유하되 오른쪽의 작은 움직임만 서로 다르다.
function drawSetPanelFx(g, set, panel, fx = null) {
  if (!set) return;
  const color = SET_PANEL_COLOR[set.id] ?? GSKIN.panel.stroke;
  const k = fx?.k ?? 1;
  const pulse = fx ? Math.sin(Math.PI * k) : 0;
  const x = panel.x + 4, y = panel.y + 4, w = panel.w - 8, h = panel.h - 8;

  // 지속 상태 — 헤더 천에만 아주 약한 색을 얹고 패널 외곽 한 겹으로 발동을 남긴다.
  g.fillStyle(color, 0.055 + pulse * 0.075);
  g.fillRect(x, y, w, 44);
  g.lineStyle(2 + pulse * 3, color, 0.34 + pulse * 0.46);
  g.strokeRoundedRect(x, y, w, h, GSKIN.r);

  // 일반 최초 발동 0.92초 — 기존 두 선을 그대로 보존한다. ◆는 아래의 세 구절로
  // 교체해 큰 세트와 완성 세트가 밝기만 다른 같은 그림이 되지 않게 한다.
  if (fx && set.level !== 'diamond') {
    const run = Math.max(0, w - 360);
    const sx = x + 180 + run * k;
    g.lineStyle(3 + pulse * 2, color, 0.34 + pulse * 0.56);
    g.lineBetween(sx - 150, y + 1, sx + 20, y + 1);
    g.lineBetween(x + w - (sx - x) - 20, y + h - 1,
                  x + w - (sx - x) + 150, y + h - 1);
  }

  // 작은 세트별 표식 — 장착 카드가 시작되는 y보다 위쪽, 패널 오른쪽 끝에만 둔다.
  const ax = panel.x + panel.w - 50, ay = panel.y + 25;
  g.lineStyle(2, color, 0.48 + pulse * 0.34);
  g.fillStyle(color, 0.42 + pulse * 0.34);
  if (set.id === 'dry') {
    for (let i = -2; i <= 2; i++) g.fillCircle(ax + i * 8, ay, 2 + (i === 0 ? pulse : 0));
  } else if (set.id === 'loose') {
    g.lineBetween(ax - 22, ay - 5, ax + 22, ay - 5);
    g.lineBetween(ax - 22, ay + 5, ax + 22, ay + 5);
  } else if (set.id === 'rough') {
    g.strokeCircle(ax - 8, ay, 7 + pulse * 3);
    g.strokeCircle(ax + 8, ay, 12 + pulse * 4);
  } else if (set.id === 'tamed') {
    g.fillTriangle(ax - 22, ay + 8, ax - 8, ay - 9, ax - 3, ay + 8);
    g.fillTriangle(ax + 2, ay + 8, ax + 15, ay - 9, ax + 22, ay + 8);
  } else if (set.id === 'aged') {
    g.strokeCircle(ax, ay, 8 + pulse * 2);
    g.strokeCircle(ax, ay, 18 + pulse * 4);
  } else {
    g.strokeRect(ax - 20, ay - 10, 40, 20);
    g.lineBetween(ax - 14, ay, ax + 14, ay);
  }

  if (set.level === 'diamond') drawDiamondSetCompletion(g, panel, fx, color);
}

// ── 방금 낀 것 — 장착 칸 연출 【사장님 지시 2026-08-02】 ──────────────
//   *"(가) 그 칸만 한 번 번쩍 + (나) 점이 차오르는 짧은 연출"*
//   ⚠ **짧아야 한다.** 상점은 멈춰 선 화면이라 여기서 길게 끌면 손이 묶인다.
// 점을 그리되 **새로 얻은 칸은 차오른다.** `from` 까지는 그대로, 그 위는 k 로 밝아진다.
//   `from` 이 null(빈 칸에 처음 끼는 것)이면 전부 차오른다.
function drawDotsFill(g, x, y, t, from, k) {
  if (isDiamondTier(t)) {
    const e = Math.min(1, Math.max(0, k));
    // 5성의 다섯 점이 잦아들고 중앙의 다이아 한 점으로 응축된다.
    for (let i = 0; i < TIER_MAX; i++) {
      g.fillStyle(TIER_COLOR[TIER_MAX - 1], 1 - e);
      g.fillCircle(x + i * 17, y, 5 * (1 - e * 0.28));
    }
    drawDiamond(g, x + ((TIER_MAX - 1) * 17) / 2, y,
      0.35 + 0.65 * e, 0.30 + 0.70 * e);
    return;
  }
  const c = tierColor(t);
  const base = from == null ? 0 : Math.min(from, t);
  for (let i = 0; i < TIER_MAX; i++) {
    if (i < base) { g.fillStyle(c, 1); g.fillCircle(x + i * 17, y, 5); continue; }
    if (i >= t) { g.fillStyle(0x3c3d3b, 1); g.fillCircle(x + i * 17, y, 5); continue; }
    // 차오르는 칸 — 빈 점 위에 밝은 점이 커지며 얹힌다
    g.fillStyle(0x3c3d3b, 1); g.fillCircle(x + i * 17, y, 5);
    const e = Math.min(1, Math.max(0, k));
    g.fillStyle(c, e);
    g.fillCircle(x + i * 17, y, 5 * (0.35 + 0.65 * e));
  }
}
// ── 무게 막대 — **급(▮). 네 칸 공통 자** ──────────────────────────
// 구 `drawCls`(세로 막대 셋, 폭 24)를 **막대로 바꾼 것**이다. 정보는 같고 자가 커졌다:
//   돌     = 「이만큼 무겁다」          나머지 = 「이만큼까지 받친다」
//   firm=false면 장착을 막지 않고 붉은 「헐거운 물림」 신호가 된다.
// ⚠ 구 함수는 남기지 않았다. 같은 것을 말하는 그림이 둘이면 반드시 한쪽이 죽는다(규율 5).
function drawWeight(g, x, y, cls, firm = true) {
  const sw = (BAR_W - 4) / 3;
  for (let i = 0; i < 3; i++) {
    g.fillStyle(i <= cls ? (firm ? 0xa9926a : 0xc96f5d) : 0x3c3d3b, 1);
    g.fillRect(Math.round(x + i * (sw + 2)), y, Math.round(sw), 10);
  }
}
// ── 축 막대 — **절대값.** 채운 길이 = 값 ÷ 그 축이 낼 수 있는 최대 ──
// `now` 를 주면 **지금 값 자리에 눈금**을 하나 세운다(상품 칸). 색이 아니라 눈금이므로
// 오버의 초록/빨강과 채널이 안 겹친다.
function drawBar(g, x, y, k, col, now = null) {
  g.fillStyle(0x0b0d0e, 1); g.fillRect(x, y, BAR_W, 8);
  const f = Math.max(0, Math.min(1, k));
  g.fillStyle(col, 0.85); g.fillRect(x, y, Math.max(1, Math.round(BAR_W * f)), 8);
  g.lineStyle(1, 0x4b4d4b, 1); g.strokeRect(x, y, BAR_W, 8);
  if (now != null) {
    const n = Math.max(0, Math.min(1, now));
    g.fillStyle(0xf1e6cf, 0.95);
    g.fillRect(Math.round(x + BAR_W * n) - 1, y - 3, 2, 14);
  }
}
function drawIconSlot(g, x, y) {
  const I = GSKIN.icon;
  g.fillStyle(I.fill, 1); g.fillRect(x, y, I.w, I.h);
  g.lineStyle(1, I.stroke, 1); g.strokeRect(x, y, I.w, I.h);
}
// ── 아이콘 칸에 **그림을 얹는다**   【2026-07-30 · 18장 도착】 ────────
// ⚠ 칸(`drawIconSlot`)은 **그대로 먼저 그린다.** 그림은 그 위에 얹히고, 그림이 없으면
//   칸만 남는다 — 즉 **빈 자리는 예전 그대로 빈 자리로 보인다**(끈 여섯이 그 상태다).
//   텍스처가 없을 때 조용히 아무 것도 안 하는 것이 아니라 **자리표시자가 사실을 말한다.**
// ⚠ 구운 그림이 정확히 96×96 = 칸과 같으므로 확대·축소가 **한 번도 없다.**
//   그래도 `setDisplaySize` 를 건다 — 나중에 다른 치수가 들어오면 칸을 넘지 않게.
function placeIcon(scene, img, x, y, slot, id) {
  const key = (slot && id) ? gearArtKey(slot, id) : null;
  if (!key || !scene.textures.exists(key)) { img.setVisible(false); return; }
  const I = GSKIN.icon;
  img.setTexture(key).setDisplaySize(I.w, I.h)
     .setPosition(Math.round(x + I.w / 2), Math.round(y + I.h / 2)).setVisible(true);
}

// ══════════════════════════════════════════════════════════════════
//  F. 막대 — **부위마다 둘.**  【사장님 지시】
//    돌 = 피해·연사 / 받침 = 발수·발당위력 / 끈 = 거리·확산 / 걸이 = 연사·궤도
//
//  ⚠ **여섯 값이 여덟 칸을 채운다.** 「발당 피해」가 돌과 받침에 둘 다 뜨고 「연사」가
//    돌과 걸이에 둘 다 뜬다 — 지시가 그렇다. 한 부위의 두 줄은 **그 부위가 움직이는
//    두 축**이고, 값 자체는 무기 전체의 값이다(네 칸이 곱이므로 부위별 값이 따로 없다).
// ══════════════════════════════════════════════════════════════════
const BAR_ROWS = {
  stone:  [['발당 피해', 'dmg'],  ['연사', 'rate']],
  cradle: [['발 수', 'shots'],    ['발당 위력', 'dmg']],
  cord:   [['사거리', 'range'],   ['흩어짐', 'spread']],
  loop:   [['연사', 'rate'],      ['도는 돌 힘', 'orbitDmg']],
};
const BAR_DP = { dmg: 1, rate: 2, shots: 0, range: 1, spread: 2, orbitDmg: 1 };
// 막대 **길이는 값의 크기**이고 이득/손해는 말하지 않는다 — 그것은 오버의 색이 한다.
// (흩어짐은 낮을수록 좋으므로 **긴 막대가 나쁜 것**이다. 라벨이 그 사실을 말한다)

// 지금 이 물맷돌의 여섯 절대값. 전투 성능표와 같은 스냅샷을 읽어 상한·벌점·낮 배율까지
// 실제 전투와 같은 결과를 쓴다. `context` 는 과거 호출처럼 축 숫자 하나여도 된다.
export function barVals(gear, context = 1) {
  const v = combatSnapshot(gear, context);
  return {
    dmg:      v.damage,
    rate:     v.rate,
    shots:    v.shots,
    range:    v.range,
    spread:   v.spread,
    orbitDmg: v.orbitHit,
  };
}
// ── 막대의 자 — **부품 표에서 실제로 뽑는다.** 지어낸 상한이 아니다 ────
// ⚠ 등급 축이 **단조**이므로 양 끝(●○○○○ · ●●●●●)만 훑으면 최대·최소가 다 잡힌다:
//   `dev(v,m)` 은 v≥1 에서 v^m(m 이 크면 크다) · v<1 은 고정,
//   `devLow(v,m)` 은 v≤1 에서 v^m(m 이 크면 **작다**) — 그래서 흩어짐의 최대는 ●○○○○ 다.
// 모든 급 조합을 장착할 수 있으므로 자도 6^4 실제 도달 조합을 전부 센다.
// ⚠ 등급을 섞으면(예: 돌만 ●5) 이 자를 조금 넘을 수 있다. 그때는 막대가 꽉 찬 채로
//   멈춘다(`drawBar` 가 0~1 로 자른다) — 숫자는 그대로 옳다.
let _barMax = null;
export function barMax() {
  if (_barMax) return _barMax;
  const M = { dmg: 0, rate: 0, shots: 0, range: 0, spread: 0, orbitDmg: 0 };
  for (const S of PARTS.stone) for (const C of PARTS.cradle)
    for (const R of PARTS.cord) for (const L of PARTS.loop) {
      const gear = { stone: { p: S, t: 1, traits: [] }, cradle: { p: C, t: 1, traits: [] },
                     cord: { p: R, t: 1, traits: [] }, loop: { p: L, t: 1, traits: [] } };
      for (const t of [1, TIER_DIAMOND]) {
        for (const k of SLOTS) gear[k].t = t;
        const v = barVals(gear);
        for (const a in M) if (v[a] > M[a]) M[a] = v[a];
      }
    }
  return (_barMax = M);
}
// 세트 한 줄 — **몇 칸이 같은 수식어인가 · 발동했는가.**  (E · F)
// ⚠ 「발동했다」는 `setOf`(4칸 같은 수식어 + 최저 등급 4 이상)가 정본이고,
//   「몇 칸」은 `setCount` 다. 둘은 다른 질문이라 둘을 다 띄운다.
// ⚠ `full` 은 **머리줄에서만** 켠다. 상품 칸은 최대 15개 변화값을 모두 보여 주므로
//   효과 문장까지 붙이면 줄이 접혀 비교표와 겹친다.
function setLine(gear, full = false) {
  const status = setRequirementStatus(gear);
  if (status.active) {
    return { txt: full ? `${status.short} — ${status.detail}` : status.short, hot: true };
  }
  if (!status.short) return { txt: '', hot: false };
  if (full) return { txt: status.short, hot: false };
  if (status.kind === 'parts') {
    return { txt: `세트 ${status.count.ko} ${status.count.n}/4 · ${4 - status.count.n}칸 부족`, hot: false };
  }
  if (status.kind === 'low') return { txt: `세트 ${status.count.ko} 4/4 · 등급 부족`, hot: false };
  return { txt: status.short, hot: false };
}

// **지금 이 물맷돌이 어떤 물건인가** — 네 부품에서 성격을 말로 뽑는다.
// 숫자만으로는 자기가 뭘 만들고 있는지 모른다.
//
// ⚠⚠ **2026-07-30 — 죽은 줄 둘을 갈아 끼웠다.** 인자는 `resolveGear` 출력이다.
//   · `m.pierce >= 3`  — 관통이 이제 `BASE_PIERCE / shots`(최대 1.0)라 **절대 안 켜진다.**
//     구 강가 돌 ●●●●● 만 6.8을 냈고 그 돌은 납작한 돌로 바뀌면서 관통을 잃었다.
//     전수 실측: 장착 가능한 6,480 조합에서 발화 **0회**(구 기준선은 580 중 51회).
//   · `m.carry`         — 오버킬 이월이 통째로 지워졌다(충돌 2). 발화 0회.
//   그 결과 **재설계된 돌 셋(고른·납작·차돌)이 화면에서 성격을 한 마디도 못 갖고**
//   있었다 — *"정체는 값이 아니라 화면에서 나온다"* 가 무너지는 자리다.
//   → 여섯 축(수·선·거리·방사·시간·반경)을 **각각 한 마디씩** 갖게 한다.
// ⚠ 줄이 길어지면 아무것도 안 읽힌다 — **네 마디에서 끊는다.**
function buildVoice(m) {
  if (!m) return '';
  const a = [];
  a.push(m.dmgMul >= 1.6 ? '한 발이 크다' : m.dmgMul <= 0.75 ? '한 발이 가볍다' : '고르게 나간다');
  // ── 여섯 축 — 그 돌이 무엇으로 답하는가 ──
  if (m.pierceInf) a.push('멈추지 않는다');                 // 성긴 고른 돌 — 선
  if (m.skips > 0) a.push(`물수제비 ${m.skips}번`);         // 거친 납작한 돌 — 거리
  // 묵은 차돌 — **근접.** 축이 궤도라 「몸 옆이 얼마나 넓은가」를 말한다.
  if (m.orbitCap > 1) a.push(m.orbitCap === Infinity
    ? '몸 옆이 몇 마리든 한꺼번에'
    : `몸 옆에서 ${Math.round(4 * m.orbitCap)}마리까지 한꺼번에`);
  // ⚠ 문턱이 `>= 5` 였고 그러면 **모난 돌 ●○○○○ 에서 안 켜졌다**(검증 지적):
  //   `STONE_AXIS.monan = byMul(8)` × `TIER_MUL[0] 0.6` = **4.8** 이라 아슬하게 미달이다.
  //   여섯 축 중 방사만 최저 등급에서 말이 없었다. 문턱은 지어내지 않았다 —
  //   방사가 아닌 돌의 파편은 `BASE_SHARDS / √발수` 라 **최대가 정확히 BASE_SHARDS(2)** 다.
  //   그것을 넘으면 그 돌은 방사가 정체인 돌이다(모난 돌의 최저가 4.8 이므로 전 등급 발화).
  if (m.shards > BASE_SHARDS) a.push('부딪히면 갈라진다');   // 길든 모난 돌 — 방사
  if (m.splash > 0)  a.push('떨어진 자리가 흔들린다');      // 굳은 무거운 돌 — 반경
  if (m.shots >= 4) a.push('한 줌씩 뿌린다');               // 마른 잔돌 — 수
  else if (m.shots >= 2) a.push('두세 개가 함께 간다');
  if (m.rateMul >= 1.25) a.push('손이 빠르다'); else if (m.rateMul <= 0.8) a.push('느리다');
  if (m.spreadMul <= 0.7) a.push('곧게 간다'); else if (m.spreadMul >= 1.5) a.push('널리 흩어진다');
  const head = a.slice(0, 4).join(' · ');
  // ── 세트 — **발동했으면 그 한 줄이 제일 크다** ──
  if (m.set) return `${head}\n『${m.set.ko}』 ${m.set.fx}`;
  // ── 아직 안 걸렸으면 **몇 칸까지 왔는지**만 말한다 ─────────────────
  // ⚠ `setSeen` 은 `resolveGear` 가 내는데 **아무도 안 읽고 있었다**(규율 5).
  //   여기서 한 줄로 닫는다. **막대(F)가 이 값을 제대로 쓸 자리**이고, 그때 이 줄은
  //   막대로 옮겨 가면 된다 — 지금은 값이 화면에 닿는 유일한 문이다.
  // ⚠ 조건을 말해 주지 않으면 「4칸을 맞춰 보자」가 애초에 안 떠오른다.
  return m.setSeen?.n >= 2 ? `${head}\n${m.setSeen.ko} ${m.setSeen.n}/4` : head;
}

export class GearUI {
  constructor(scene, shop) {
    this.scene = scene; this.shop = shop;
    this.hover = -1; this.hoverReroll = false;
    this.rulesOpen = false;
    this.rules = combatRuleCopy();
    this.boxes = shopBoxes();
    this.worn = wornBoxes();

    const mk = (size, color, ox = 0, wrap = 0) => scene.add.text(0, 0, '', {
      fontFamily: GSKIN.font, fontSize: size, color,
      ...(wrap ? { wordWrap: { width: wrap } } : {}),
    }).setOrigin(ox, 0).setDepth(GSKIN.depth + 2).setScrollFactor(0).setVisible(false);

    this.g = scene.add.graphics().setDepth(GSKIN.depth).setScrollFactor(0).setVisible(false);
    // 카드 천보다 위, 아이콘·글자보다 아래. ◆ 결속선이 카드 바탕 뒤로 사라지지 않되
    // 이름과 수치의 가독성은 침범하지 않는 전용 깊이다.
    this.setFxG = scene.add.graphics().setDepth(GSKIN.depth + 0.75).setScrollFactor(0).setVisible(false);
    this.cardG = scene.add.graphics().setDepth(GSKIN.depth + 0.5).setScrollFactor(0).setVisible(false);
    this.rulesG = scene.add.graphics().setDepth(GSKIN.depth + 20).setScrollFactor(0).setVisible(false);
    this.title  = mk(GSKIN.fs.title, '#d9d1c2', 0.5);
    this.budget = mk(GSKIN.fs.name, INK, 0.5);
    this.voice  = mk('14px', '#c8ae78', 0.5, LEFT_W - 72);
    this.statHdr = mk(GSKIN.fs.label, '#9e9b92');
    this.voiceHdr = mk(GSKIN.fs.label, '#9e9b92');
    this.statHero = {
      key: mk(GSKIN.fs.label, DIM),
      val: mk(GSKIN.fs.name, '#e5bd68', 1),
    };
    this.statNote = mk('14px', DIM);
    // 대표 위력 아래에 실제 적용 세부값을 최대 아홉 개까지 놓는다. 일반 돌은 기본 여덟
    // 축 + 돌 고유축 둘, 차돌은 투사 수치 대신 강타·내측·반경·동시 타격을 쓴다.
    this.statT = Array.from({ length: 9 }, () => ({
      key: mk(GSKIN.fs.label, DIM),
      val: mk(GSKIN.fs.body, INK, 1),
    }));
    this.foot   = mk(GSKIN.fs.body, INK, 0.5);
    this.rerollTxt = mk(GSKIN.fs.body, INK, 0.5);
    this.rulesButtonTxt = mk(GSKIN.fs.body, INK, 0.5).setDepth(GSKIN.depth + 3);
    this.rulesTitle = mk('32px', '#d9d1c2', 0.5).setDepth(GSKIN.depth + 22);
    this.rulesStatus = mk(GSKIN.fs.body, '#e5bd68', 0, 1240).setDepth(GSKIN.depth + 22);
    this.rulesBody = mk(GSKIN.fs.body, INK, 0, 1240).setDepth(GSKIN.depth + 22);
    this.rulesCloseTxt = mk(GSKIN.fs.body, INK, 0.5).setDepth(GSKIN.depth + 22);
    this.rulesFoot = mk(GSKIN.fs.body, DIM, 0.5).setDepth(GSKIN.depth + 22);
    this.wornHdr = mk(GSKIN.fs.label, DIM, 0);
    this.setFxTitle = mk(GSKIN.fs.body, DIM, 0.5).setDepth(GSKIN.depth + 3);
    this.setFxBody = mk(GSKIN.fs.label, DIM, 0.5).setDepth(GSKIN.depth + 3);
    this.equipFx = null;
    this.setFx = null;
    this._fxSeq = null;
    this.uiT = 0;

    // 강화창은 전투용 64px 보행 프레임을 확대하지 않는다. 물매를 직접 수선하는
    // 전용 320px 장면을 1:1로 놓아 인물·행동·소품이 한 번에 읽히게 한다.
    this.hiRes = true;
    this.david = scene.add.sprite(0, 0, 'watchman-shop-portrait')
      .setOrigin(0.5).setDepth(GSKIN.depth + 1).setScrollFactor(0).setVisible(false);
    scene.textures.get('watchman-shop-portrait').setFilter(Phaser.Textures.FilterMode.NEAREST);

    // ── 부위 아이콘 여덟 — **장착 4 + 상품 4** ───────────────────────
    // 칸은 `graphics` 가 그리지만 그림은 텍스처이므로 스프라이트가 필요하다.
    // 매 프레임 만들지 않고 여덟 개를 **여기서 한 번** 만들고 `placeIcon` 이 옮긴다.
    //   깊이: 패널(depth) 위 · 글자(depth+2) 아래 → **글자가 그림에 안 가린다**
    //   `'__DEFAULT'` 로 만든다 — Phaser 내장 1×1 텍스처다. 첫 `placeIcon` 이 갈아 끼운다.
    const mkIcon = () => scene.add.image(0, 0, '__DEFAULT')
      .setDepth(GSKIN.depth + 1).setScrollFactor(0).setVisible(false);
    this.wornIcon = SLOTS.map(mkIcon);
    this.offIcon = SLOTS.map(mkIcon);
    // 카드와 챙겨두기 자물쇠는 전용 PNG다. 등급선과 상태 워시만 Graphics가 맡는다.
    this.cardSkin = SLOTS.map(() => scene.add.image(0, 0, 'gearshop-card-fabric')
      .setOrigin(0).setDepth(GSKIN.depth + 0.2).setScrollFactor(0).setVisible(false));
    this.keepLocks = SLOTS.map(() => scene.add.image(0, 0, 'gearshop-padlock')
      .setDepth(GSKIN.depth + 4).setScrollFactor(0).setVisible(false));
    for (const key of ['gearshop-card-fabric', 'gearshop-padlock']) {
      if (scene.textures.exists(key)) scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
    }
    // 구운 아이콘은 **1:1 로 놓이므로** 보간이 한 번도 안 일어난다. 그래도 NEAREST 로
    // 못 박는다 — `pixelArt` 설정에 기대면 게임 설정이 바뀌는 날 조용히 흐려진다.
    for (const [slot, ids] of Object.entries(GEAR_ART)) {
      for (const id of ids) {
        const k = gearArtKey(slot, id);
        if (scene.textures.exists(k)) {
          scene.textures.get(k).setFilter(Phaser.Textures.FilterMode.NEAREST);
        }
      }
    }

    // ⚠ 장착 칸도 **막대 두 줄**을 갖는다. 상품 칸과 세로로 나란히 서야 「지금 이만큼 →
    //   사면 이만큼」이 눈으로 이어진다(파일 머리 「x축을 공유한다」).
    this.wornT = SLOTS.map(() => ({
      slot: mk(GSKIN.fs.label, DIM), name: mk(GSKIN.fs.body, INK),
      b0: mk(GSKIN.fs.label, DIM), b1: mk(GSKIN.fs.label, DIM),
    }));
    this.slotT = SLOTS.map(() => {
      const per = mk(GSKIN.fs.label, DIM);
      per.setFixedSize?.(CANDIDATE_LEAD_MAX_W, 22);
      per.setMaxLines?.(1);
      return {
        key:  mk(GSKIN.fs.label, '#91b578', 1),
        slot: mk(GSKIN.fs.label, DIM),
        name: mk(GSKIN.fs.name, INK, 0, COL_W - 28).setDepth(GSKIN.depth + 5),
        per,
        price: mk(GSKIN.fs.body, '#e5bd68', 1).setDepth(GSKIN.depth + 5),
        // 막대 두 줄 + 세트 한 줄 — **오버와 무관하게 항상 보인다**(F)
        b0:  mk(GSKIN.fs.label, INK), b1: mk(GSKIN.fs.label, INK),
        set: mk(GSKIN.fs.label, DIM, 0, COL_W - 28),
        // 실제 적용식은 투사↔차돌 전환에서 최대 15축이 동시에 바뀐다. 15줄을 모두 만들어
        // 세부 효과를 버리지 않고, 작은 비교 글꼴과 19px 리듬으로 가격줄 위에 정확히 넣는다.
        // `gearshop_check.mjs` 가 전 조합 최대 행과 최악 렌더의 하단 여유를 다시 잰다.
        rows: Array.from({ length: 15 }, () => ({
          ko: mk('14px', DIM), val: mk('14px', INK, 1),
        })),
      };
    });

    // ── 입력 ── **상점은 「이동 하나」의 유일한 예외다**(명세 3장)
    const kb = scene.input.keyboard;
    this._keys = [];
    const on = (name, fn) => { const h = e => { if (this.shop.open) { e.preventDefault?.(); fn(); } };
      kb.on(`keydown-${name}`, h); this._keys.push([name, h]); };
    on('ONE', () => this.buy(0)); on('TWO', () => this.buy(1));
    on('THREE', () => this.buy(2)); on('FOUR', () => this.buy(3));
    on('NUMPAD_ONE', () => this.buy(0)); on('NUMPAD_TWO', () => this.buy(1));
    on('NUMPAD_THREE', () => this.buy(2)); on('NUMPAD_FOUR', () => this.buy(3));
    on('Q', () => this.lock(0)); on('W', () => this.lock(1));
    on('E', () => this.lock(2)); on('T', () => this.lock(3));
    on('R', () => this.doReroll());
    on('TAB', () => this.toggleRules());
    on('SPACE', () => this.close());
    scene.input.on('pointermove', p => {
      if (!this.shop.open) return;
      if (this.rulesOpen) {
        this.hover = -1;
        this.hoverReroll = false;
        return;
      }
      this.hover = this.hitTest(p.x, p.y);
      this.hoverReroll = this.hitReroll(p.x, p.y);
    });
    scene.input.on('gameout', () => {
      this.hover = -1;
      this.hoverReroll = false;
    });
    scene.input.on('pointerdown', p => {
      if (!this.shop.open) return;
      if (this.rulesOpen) {
        if (this.hitRulesClose(p.x, p.y)) this.toggleRules(false);
        return;
      }
      if (this.hitRulesButton(p.x, p.y)) { this.toggleRules(true); return; }
      const i = this.hitTest(p.x, p.y);
      if (i >= 0) { if (p.event?.shiftKey) this.lock(i); else this.buy(i); }
      else if (this.hitReroll(p.x, p.y)) this.doReroll();
    });
  }

  destroy() {
    const kb = this.scene.input.keyboard;
    for (const [name, h] of this._keys) kb.off(`keydown-${name}`, h);
  }

  hitTest(x, y) {
    for (let i = 0; i < this.boxes.length; i++) {
      const b = this.boxes[i];
      if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) return i;
    }
    return -1;
  }
  hitReroll(x, y) {
    const rx = W / 2 - 190;
    return x >= rx && x <= rx + 380 && y >= H - 118 && y <= H - 62;
  }
  hitRulesButton(x, y) {
    const b = rulesButtonBox();
    return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
  }
  hitRulesClose(x, y) {
    const b = rulesCloseBox();
    return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
  }

  toggleRules(force = null) {
    if (!this.shop.open) return false;
    this.rulesOpen = force == null ? !this.rulesOpen : !!force;
    if (this.rulesOpen) {
      this.hover = -1;
      this.hoverReroll = false;
    }
    this.render();
    return this.rulesOpen;
  }
  buy(i) { if (!this.rulesOpen && this.shop.buy(i)) this.scene.onShopAction?.('buy'); }
  lock(i) { if (!this.rulesOpen && this.shop.lock(i)) this.scene.onShopAction?.('lock'); }
  doReroll() { if (!this.rulesOpen && this.shop.reroll()) this.scene.onShopAction?.('reroll'); }
  close() {
    if (this.rulesOpen) { this.toggleRules(false); return; }
    this.shop.close(); this.scene.onShopAction?.('close');
  }
  select(delta = 0) {
    if (this.rulesOpen) return;
    const count = Math.min(this.boxes.length, this.shop.offers?.length ?? 0);
    if (count <= 0) return;
    const start = this.hover >= 0 && this.hover < count ? this.hover : 0;
    this.hover = ((start + delta) % count + count) % count;
    this.hoverReroll = false;
  }
  buySelected() { if (!this.rulesOpen && this.hover >= 0) this.buy(this.hover); }
  lockSelected() { if (!this.rulesOpen && this.hover >= 0) this.lock(this.hover); }

  hideAll() {
    // ⚠ **연출은 상점이 닫히면 끝난다.** 안 지우면 다음에 열었을 때 **지난 번쩍임이
    //   남아** 「방금 이걸 샀다」가 거짓이 된다. 창이 닫히는 이 자리가 유일한 종점이다.
    this.equipFx = null;
    this.setFx = null;
    this.rulesOpen = false;
    // 닫힌 동안 들어온 마지막 장착 사건도 여기서 소비한다. 그러지 않으면 다음 상점이
    // 열릴 때 지난 구매를 새 구매로 오인해 장착/세트 폭죽을 다시 재생한다.
    const closedEquip = this.shop.lastEquip;
    if (closedEquip) this._fxSeq = closedEquip.seq;
    this.hover = -1;
    this.hoverReroll = false;
    this.g.setVisible(false); this.setFxG.setVisible(false); this.cardG.setVisible(false);
    this.rulesG.setVisible(false);
    this.david.setVisible(false);
    for (const im of [...this.wornIcon, ...this.offIcon, ...this.cardSkin, ...this.keepLocks]) im.setVisible(false);
    for (const t of [this.title, this.budget, this.voice, this.statHdr, this.voiceHdr,
                     this.statHero.key, this.statHero.val, this.statNote,
                     this.foot, this.rerollTxt, this.rulesButtonTxt,
                     this.rulesTitle, this.rulesStatus, this.rulesBody,
                     this.rulesCloseTxt, this.rulesFoot, this.wornHdr,
                     this.setFxTitle, this.setFxBody]) t.setVisible(false);
    for (const t of this.statT) { t.key.setVisible(false); t.val.setVisible(false); }
    for (const t of this.wornT) for (const k of Object.values(t)) k.setVisible(false);
    for (const t of this.slotT) {
      for (const [k, v] of Object.entries(t)) {
        if (k === 'rows') for (const r of v) { r.ko.setVisible(false); r.val.setVisible(false); }
        else v.setVisible(false);
      }
    }
  }

  draw() { this.render(); }

  // ══════════════════════════════════════════════════════════════════
  //  **방금 낀 것을 장착 칸이 그 자리에서 말한다** 【사장님 지시 2026-08-02】
  //    *"사면 지금 장착 파츠에 반영되는 느낌으로."*  → 그 칸만 한 번 번쩍이고(ⓐ),
  //    오른 점이 차오른다(ⓑ). 카드는 사실만 말하고, **결과는 여기서 보인다.**
  //
  //  ⚠⚠ **자기 시계를 든다.** 상점이 열려 있는 동안은 `world.step` 을 아예 안 부르므로
  //    `world.t` 도 `scene._artClock` 도 **멈춰 있다**(scene.js 「전투는 멈춘다」 규율).
  //    그 시계로 연출을 돌리면 한 프레임도 안 움직인다 — 실제로 그렇게 짤 뻔했다.
  //    그래서 `scene._dtReal`(얼어도 매 프레임 갱신된다)로 여기서 따로 잰다.
  //  ⚠ 상점이 **닫히는 순간 연출도 끝난다.** 다음에 열었을 때 지난 번쩍임이 남아 있으면
  //    「방금 산 것」이 거짓이 된다 — `hideAll` 에서 지운다.
  // ══════════════════════════════════════════════════════════════════
  _tickFx() {
    this.uiT = (this.uiT ?? 0) + (this.scene._dtReal ?? 0);
    const le = this.shop.lastEquip;
    if (le && le.seq !== this._fxSeq) {          // 새로 산 것이 있다
      this._fxSeq = le.seq;
      this.equipFx = { ...le, t0: this.uiT };
      if (le.setActivated && le.set) this.setFx = { ...le.set, seq: le.seq, t0: this.uiT };
    }
    if (this.equipFx && this.uiT - this.equipFx.t0 > EQUIP_FX.sec) this.equipFx = null;
    if (this.setFx) {
      const life = this.setFx.level === 'diamond' ? DIAMOND_SET_PANEL_FX_SEC : SET_PANEL_FX_SEC;
      if (this.uiT - this.setFx.t0 > life) this.setFx = null;
    }
  }
  // 그 칸의 연출 진행도 0~1. 해당 칸이 아니거나 끝났으면 null.
  _fxFor(slot) {
    const f = this.equipFx;
    if (!f || f.slot !== slot) return null;
    const k = (this.uiT - f.t0) / EQUIP_FX.sec;
    return k >= 0 && k <= 1 ? { ...f, k } : null;
  }
  // 세트가 아직 실제로 발동 중일 때만 최초 발동 연출을 돌린다. 반복 render는 같은
  // 일련번호의 t0를 다시 만들지 않으므로 0.92/1.12초가 매 프레임 처음으로 돌아가지 않는다.
  _setFxFor(activeSet) {
    const f = this.setFx;
    if (!f || !activeSet || f.id !== activeSet.id || f.level !== activeSet.level) return null;
    const life = f.level === 'diamond' ? DIAMOND_SET_PANEL_FX_SEC : SET_PANEL_FX_SEC;
    const k = (this.uiT - f.t0) / life;
    return k >= 0 && k <= 1 ? { ...f, k } : null;
  }

  renderRules() {
    const texts = [this.rulesTitle, this.rulesStatus, this.rulesBody,
      this.rulesCloseTxt, this.rulesFoot];
    if (!this.rulesOpen) {
      this.rulesG.setVisible(false).clear();
      for (const t of texts) t.setVisible(false);
      return;
    }

    const g = this.rulesG, p = rulesPanelBox(), close = rulesCloseBox();
    g.setVisible(true).clear();
    g.fillStyle(0x050607, 0.92); g.fillRect(0, 0, W, H);
    g.fillStyle(GSKIN.panel.fill, 1); g.fillRoundedRect(p.x, p.y, p.w, p.h, GSKIN.r);
    g.lineStyle(3, GSKIN.panel.stroke, 1); g.strokeRoundedRect(p.x, p.y, p.w, p.h, GSKIN.r);
    g.fillStyle(0x9f8961, 0.9); g.fillRect(p.x, p.y, 8, p.h);
    g.fillStyle(0x25292b, 1); g.fillRoundedRect(close.x, close.y, close.w, close.h, GSKIN.r);
    g.lineStyle(2, 0xa9926a, 1); g.strokeRoundedRect(close.x, close.y, close.w, close.h, GSKIN.r);

    const status = setRequirementStatus(this.shop.gear);
    const statusText = status.active
      ? `현재 장착 · ${status.short} — ${status.detail}`
      : status.short
        ? `현재 장착 · ${status.short}\n${status.detail}`
        : '현재 장착 · 같은 수식어가 2칸 이상 모이면 세트 진행 상태가 표시됩니다.';
    const r = this.rules;
    const body = [
      '위력',
      r.formula,
      '연사',
      r.rate,
      r.rateCap,
      '관통',
      r.pierce,
      '치명타',
      r.crit,
      r.bear,
      '물림',
      ...r.grip.map((line) => `• ${line}`),
      '세트',
      ...r.sets.map((line) => `• ${line}`),
      '결합',
      r.merge,
    ].join('\n');

    this.rulesTitle.setPosition(p.x + p.w / 2, p.y + 24)
      .setText('전투 수치와 세트 규칙').setVisible(true);
    this.rulesCloseTxt.setPosition(close.x + close.w / 2, close.y + 10)
      .setText('[Tab] 닫기').setVisible(true);
    this.rulesStatus.setPosition(p.x + 70, p.y + 92).setText(statusText).setVisible(true);
    this.rulesBody.setPosition(p.x + 70, p.y + 176).setText(body).setVisible(true);
    this.rulesFoot.setPosition(p.x + p.w / 2, p.y + p.h - 48)
      .setText('Tab 또는 Space · 상점으로 돌아가기').setVisible(true);
  }

  candidatePreviews() {
    const offers = this.shop.offers ?? [];
    const key = `${this.shop.lastEquip?.seq ?? 0}|${offers.map((offer) =>
      `${offer.card?.id ?? '-'}:${offer.bought ? 1 : 0}`).join('|')}`;
    if (key !== this._candidatePreviewKey) {
      this._candidatePreviewKey = key;
      // 상점은 멈춘 화면에서도 매 프레임 그려진다. 실제 비교식은 제시/구매가 바뀔
      // 때만 다시 풀고, 호흡 애니메이션 프레임마다 네 후보를 재계산하지 않는다.
      this._candidatePreviews = offers.map((_, i) => this.shop.preview(i) ?? []);
    }
    return this._candidatePreviews ?? [];
  }

  render() {
    const s = this.shop;
    if (!s.open) { this.hideAll(); return; }
    this._tickFx();
    const g = this.g;
    g.setVisible(true).clear();
    const setG = this.setFxG;
    setG.setVisible(true).clear();
    const cg = this.cardG;
    cg.setVisible(true).clear();
    g.fillStyle(GSKIN.dim.color, GSKIN.dim.alpha); g.fillRect(0, 0, W, H);
    // 전투 장면을 단순히 어둡게 비치는 것이 아니라, 장비를 펼쳐 놓는 독립 화면이다.
    g.fillStyle(0x101213, 1); g.fillRect(0, 0, W, H);
    g.fillStyle(0x30383a, 0.42); g.fillRect(0, 0, W, 5);
    g.fillRect(0, H - 5, W, 5);
    // 화면 전체를 가르던 장식용 가로 눈금은 제거한다. 상점의 구획은 실제 패널 테두리만
    // 맡고, 빈 배경에는 반복선을 그리지 않는다.

    // 오버한 칸이 어느 부위인가 — 위쪽 장착 줄을 물들이는 데 쓴다
    const hov = this.hover >= 0 ? s.offers[this.hover] : null;
    const hovStone = hov && hov.part.slot === 'stone' ? hov.part.p : null;

    this.title.setPosition(W / 2, 52).setText('물맷돌을 고쳐 맨다').setVisible(true);
    // **「하나는 버린다」를 화면이 말한다** — 4칸인데 살 수 있는 것은 3이다.
    // **연속 레벨업**이면 그 사실을 크게 말한다 — 한꺼번에 오른 것이 사건이어야 한다
    const burst = s.burst ?? 1;
    const AX = 1 + 0.1 * Math.min(1, Math.max(0, s.w.axis ?? 0));
    const statContext = { axis: AX, dayIn: s.w?.dayIn };
    const nowStats = combatSnapshot(s.gear, statContext);
    const wornLoose = new Set(looseSlots(s.gear));
    const hoverLoose = new Set(hov ? looseSlots(s.gearAfter(this.hover)) : wornLoose);
    const currentRows = performanceRows(s.gear, statContext);
    const nowV = barVals(s.gear, statContext);
    const candidatePreviews = this.candidatePreviews();
    this.budget.setPosition(W / 2, 98)
      .setText(burst > 1
        ? `한꺼번에 ${burst}단계 올랐다 —  살 수 있는 것  ${s.buysLeft}`
        : `살 수 있는 것  ${s.buysLeft} / ${s.offers.length}`)
      .setColor(burst > 1 ? '#e5bd68' : s.buysLeft > 0 ? INK : BAD).setVisible(true);

    // ── 왼쪽: 다윗 ──────────────────────────────────────────────
    const lx = PAD, ly = TOP_Y, lh = BOT_Y + BOT_H - TOP_Y;
    g.fillStyle(GSKIN.panel.fill, 1); g.fillRoundedRect(lx, ly, LEFT_W, lh, GSKIN.r);
    g.lineStyle(2, GSKIN.panel.stroke, 1); g.strokeRoundedRect(lx, ly, LEFT_W, lh, GSKIN.r);
    // 수선 장면은 원본 320px 그대로 쓴다. 원형 마스크를 없애 손과 물매가 잘리지 않는다.
    const portraitX = lx + 20, portraitY = ly + 66, portraitSize = 320;
    g.fillStyle(0x090b0c, 1); g.fillRect(portraitX, portraitY, portraitSize, portraitSize);
    g.lineStyle(2, 0x596064, 0.92); g.strokeRect(portraitX, portraitY, portraitSize, portraitSize);
    g.lineStyle(1, 0xbbb4a5, 0.20);
    g.strokeRect(portraitX + 7, portraitY + 7, portraitSize - 14, portraitSize - 14);
    this.david.setPosition(Math.round(lx + LEFT_W / 2), Math.round(portraitY + portraitSize / 2))
      .setScale(1).setVisible(true);

    // 성향과 수치를 한 장의 전투 요약으로 묶는다. 대표 화력을 먼저 읽고, 그 아래에는
    // 현재 돌에 실제로 적용되는 축만 펼친다. 차돌이면 투사 수치 대신 궤도 강타가 주인공이다.
    const { x: statsX, y: statsY, w: statsW, h: statsH } = performanceBox();
    g.fillStyle(0x0c0e0f, 1); g.fillRect(statsX, statsY, statsW, statsH);
    g.lineStyle(3, 0x596064, 1); g.strokeRect(statsX, statsY, statsW, statsH);
    g.fillStyle(0x9f8961, 0.9); g.fillRect(statsX, statsY, 7, statsH);
    this.statHdr.setPosition(statsX + 18, statsY + 12).setText('현재 전투 성능').setVisible(true);
    const hero = currentRows[0];
    this.statHero.key.setPosition(statsX + 18, statsY + 43)
      .setText(hero?.label ?? '주력 위력/초').setVisible(true);
    this.statHero.val.setPosition(statsX + statsW - 18, statsY + 35)
      .setText(hero?.text ?? '—').setVisible(true);
    this.statNote.setPosition(statsX + 18, statsY + 73)
      .setText(nowStats.meleeOnly
        ? '강타 1마리 기준 · 실제 주기·반경 반영'
        : '직격 1마리 기준 · 상한·벌점·낮 효과 반영')
      .setVisible(true);
    g.lineStyle(1, 0x596064, 0.68);
    g.lineBetween(statsX + 18, statsY + 95, statsX + statsW - 16, statsY + 95);
    const detailRows = currentRows.slice(1, 10);
    for (let i = 0; i < this.statT.length; i++) {
      const rowData = detailRows[i], t = this.statT[i];
      if (!rowData) { t.key.setVisible(false); t.val.setVisible(false); continue; }
      const col = i % 2, row = Math.floor(i / 2);
      const cellX = statsX + 18 + col * 154;
      t.key.setPosition(cellX, statsY + 105 + row * 31).setText(rowData.label).setVisible(true);
      t.val.setPosition(cellX + 136, statsY + 121 + row * 31).setText(rowData.text).setVisible(true);
    }
    g.lineBetween(statsX + 18, statsY + 272, statsX + statsW - 16, statsY + 272);
    this.voiceHdr.setPosition(statsX + 18, statsY + 282).setText('전투 성향').setVisible(true);
    this.voice.setPosition(statsX + statsW / 2, statsY + 306)
      .setText(buildVoice(resolveGear(s.gear))).setVisible(true);

    // ── 오른쪽 위: 현재 장착 4칸 (가로 한 줄) ────────────────────
    const wornPanel = wornPanelBox();
    g.fillStyle(GSKIN.panel.fill, 1);
    g.fillRoundedRect(wornPanel.x, wornPanel.y, wornPanel.w, wornPanel.h, GSKIN.r);
    g.lineStyle(2, GSKIN.panel.stroke, 1);
    g.strokeRoundedRect(wornPanel.x, wornPanel.y, wornPanel.w, wornPanel.h, GSKIN.r);
    // ── 세트 — 장착 패널 안에서만 발동 상태를 말한다 ─────────────────
    // 상품 카드의 등급색은 그대로 두고, 현재 정본의 수식어·효과 문구만 패널 헤더에
    // 올린다. 미발동 2/4·3/4 진행도는 기존 머리줄을 그대로 유지한다.
    const activeSet = setOf(s.gear);
    const setProgress = setLine(s.gear, true);
    const setPulse = this._setFxFor(activeSet);
    drawSetPanelFx(setG, activeSet, wornPanel, setPulse);
    this.wornHdr.setPosition(RIGHT_X + 14, TOP_Y + 12)
      .setText(`지금 매고 있는 것${wornLoose.size
        ? `  ·  ⚠ 헐거운 물림 · 돌 피해 전달 ${Math.round(nowStats.support * 100)}%`
        : nowStats.support < 1 ? `  ·  돌 피해 전달 ${Math.round(nowStats.support * 100)}%`
        : ''}${!activeSet && setProgress.txt ? `  ·  ${setProgress.txt}` : ''}`)
      .setColor(wornLoose.size ? BAD : DIM).setVisible(true);
    if (activeSet) {
      const css = SET_PANEL_CSS[activeSet.id] ?? DIM;
      const diamond = activeSet.level === 'diamond';
      const level = activeSet.level === 'large' ? '크게' : '작게';
      const diamondFx = activeSet.diamondFx ?? activeSet.fx;
      this.setFxTitle.setPosition(wornPanel.x + wornPanel.w / 2, wornPanel.y + 5)
        .setText(diamond ? `◆ 『${activeSet.ko}』 세트 완성` : `『${activeSet.ko}』 세트 발동`)
        .setColor(diamond ? TIER_CSS[TIER_DIAMOND - 1] : css).setAlpha(1).setVisible(true);
      this.setFxBody.setPosition(wornPanel.x + wornPanel.w / 2, wornPanel.y + 29)
        .setText(diamond ? `◆ 완성 · ${diamondFx}` : `${level} · ${activeSet.fx}`)
        .setColor(diamond ? TIER_CSS[TIER_DIAMOND - 1] : css).setAlpha(0.86).setVisible(true);
    } else {
      this.setFxTitle.setVisible(false);
      this.setFxBody.setVisible(false);
    }

    // 막대의 자와 지금 값 — **프레임당 한 번만** 뽑는다(칸마다 다시 풀면 4배가 된다)
    const MAX = barMax();

    for (let i = 0; i < SLOTS.length; i++) {
      const b = this.worn[i], key = SLOTS[i], cur = s.gear[key], t = this.wornT[i];
      // 오버한 후보를 장착한 뒤 헐거워지는 부위를 붉게 짚는다. 빨강은 구매 금지가
      // 아니라 실제 좋은 효과 전달 손실이다. 평시에도 아래 무게 막대가 같은 신호를 낸다.
      let edge = cur ? tierColor(cur.t) : GSKIN.panel.stroke, ew = 2;
      if (hov && key !== 'stone' && cur
          && (hovStone || hov.part.slot === key)) {
        edge = hoverLoose.has(key) ? 0xc96f5d : 0x91b578; ew = 4;
      }
      // ⓐ **방금 이 칸에 앉았는가** — 그렇다면 한 번 번쩍인다(사장님 지시 2026-08-02).
      //   바탕이 밝아졌다 가라앉고, 바깥에 고리가 퍼지며 사라진다. 0.45초.
      const fx = this._fxFor(key);
      const fk = fx ? 1 - fx.k : 0;                     // 1 → 0 으로 잦아든다
      const fp = fx ? Math.sin(Math.PI * fx.k) : 0;
      g.fillStyle(0x0b0d0e, 1); g.fillRoundedRect(b.x, b.y, b.w, b.h, GSKIN.r);
      if (fx) {
        g.fillStyle(tierColor(fx.to), Math.min(0.48, 0.24 + 0.22 * fp));
        g.fillRoundedRect(b.x, b.y, b.w, b.h, GSKIN.r);
      }
      g.lineStyle(fx ? ew + 3 : ew, fx ? tierColor(fx.to) : edge, 1);
      g.strokeRoundedRect(b.x, b.y, b.w, b.h, GSKIN.r);
      if (fx) {
        const sp = Math.min(12, EQUIP_FX.ring + EQUIP_FX.spread * fx.k);
        g.lineStyle(4, tierColor(fx.to), Math.min(1, 0.28 + 0.72 * fk + 0.25 * fp));
        g.strokeRoundedRect(b.x - sp, b.y - sp, b.w + sp * 2, b.h + sp * 2, GSKIN.r + sp);
        g.lineStyle(3, 0xffe5a2, 0.58 * fp);
        g.strokeRoundedRect(b.x + 6, b.y + 6, b.w - 12, b.h - 12, GSKIN.r);
      }
      const iy = b.y + (b.h - GSKIN.icon.h) / 2;
      drawIconSlot(g, b.x + 12, iy);
      placeIcon(this.scene, this.wornIcon[i], b.x + 12, iy, key, cur?.p?.id);
      const tx = b.x + 12 + GSKIN.icon.w + 12, bx = barX(b);
      t.slot.setPosition(tx, b.y + 6).setText(SLOT_KO[key]).setVisible(true);
      t.name.setPosition(tx, b.y + 24).setText(cur ? cur.p.ko : '—')
        .setColor(cur ? tierCss(cur.t) : INK).setVisible(true);
      if (cur) {
        // ⓑ 오른 점이 **차오른다.** 안 오른 칸은 평소대로 그린다.
        if (fx) drawDotsFill(g, tx + 3, b.y + 66, cur.t, fx.from, fx.k);
        else drawDots(g, tx + 3, b.y + 66, cur.t);
        // 무게 막대 — **오른쪽 정렬.** 아래 상품 칸의 막대와 x 가 같다
        drawWeight(g, bx, b.y + 78, cur.p.cls,
                   key === 'stone' || !(hov ? hoverLoose : wornLoose).has(key));
      }
      // 부위별 두 줄 — **지금 값 그대로.** 오버 여부와 무관하게 항상 있다
      const rows = BAR_ROWS[key];
      for (let r = 0; r < 2; r++) {
        const [ko, ax] = rows[r], v = nowV[ax], y = b.y + 96 + r * 22;
        drawBar(g, bx, y + 2, MAX[ax] ? v / MAX[ax] : 0, tierColor(cur?.t ?? 1));
        (r ? t.b1 : t.b0).setPosition(tx, y).setText(`${ko} ${v.toFixed(BAR_DP[ax])}`)
          .setVisible(true);
      }
    }

    // ── 오른쪽 아래: 살 것 4칸 ──────────────────────────────────
    // ⚠ 아이콘은 **먼저 넷을 다 숨긴다.** 상품이 넷보다 적은 프레임에서 남는 스프라이트가
    //   지난 프레임 그림을 그대로 들고 있으면 「없는 상품의 그림」이 화면에 남는다.
    for (const im of [...this.offIcon, ...this.cardSkin, ...this.keepLocks]) im.setVisible(false);
    for (let i = 0; i < s.offers.length; i++) {
      const o = s.offers[i], b = this.boxes[i], c = o.card, t = this.slotT[i];
      const after = s.gearAfter(i);
      const candidateLoose = new Set(c.grip?.loose ?? looseSlots(after));
      const candidatePartLoose = o.part.slot === 'stone'
        ? candidateLoose.size > 0 : candidateLoose.has(o.part.slot);
      const preview = candidatePreviews[i] ?? [];
      const lead = candidateLeadChange(preview);
      // 카드마다 **구매 뒤 실제 장착 결과 등급** 가격이 다르다. 같은 5성을 다시 사서
      // ◆가 되는 칸도 이 한 함수가 12치즈로 표시하고 실제 buy와 정확히 맞춘다.
      const price = s.priceFor(i);
      // ══════════════════════════════════════════════════════════
      //  같은 부품이라 **등급이 오르는** 칸인가 【사장님 지시 2026-08-02 — 되돌림】
      //
      //  ⚠ 첫 판은 이 칸을 **오른 뒤 등급으로** 그렸다(점·테두리·이름색 전부).
      //    그건 카드가 거짓말을 하는 것이다 — 놓인 물건은 지금 낀 것과 **같은 등급**이고,
      //    그렇게 그리면 **옆 칸의 진짜 상위 등급 카드와 구별이 안 된다.**
      //  사장님 지시: *"동일한 등급의 파츠로 하되, 거기 위에 뭔가 강화 가능하다는
      //  표시 정도만."*  → 카드 몸은 **원래 등급 그대로**, 표시는 아래 둘로만 한다:
      //      ⓐ 모서리 배지 ▲      ⓑ 바깥 테두리 한 겹
      //  둘 다 **오른 뒤 등급 색**이다 — 「사면 이 색이 된다」를 색으로만 귀띔한다.
      //  실제로 무엇이 되는지는 **장착 칸**이 사는 순간에 보여 준다(equipFx).
      // ══════════════════════════════════════════════════════════
      const up = !!c.upgrade;
      const upT = up ? c.upgrade.to : o.part.t;        // 배지·바깥테두리 색만 이걸 쓴다
      const pointed = this.hover === i;
      const state = o.bought ? 'bought'
        : (s.buysLeft <= 0 || s.wallet < price) ? 'poor'
        : (pointed ? 'hover' : 'idle');
      // 호버·구매·잠금 상태와 무관하게 카드 외곽선은 항상 놓인 부품의 등급색이다.
      const edge = tierColor(o.part.t);
      this.cardSkin[i].setPosition(b.x, b.y).setDisplaySize(b.w, b.h).setVisible(true);
      if (pointed) {
        const hp = 0.5 + 0.5 * Math.sin(this.uiT * 7 + i * 0.6);
        const wash = GSKIN.hover.washAlpha;
        // 카드 안을 다시 나누는 금선은 없다. 등급색이 직물 전체를 한 덩어리로 물들이고,
        // 같은 등급색의 실제 외곽선만 밝아져 현재 커서 위치를 말한다.
        cg.fillStyle(edge, wash * (0.88 + 0.12 * hp));
        cg.fillRect(b.x, b.y, b.w, b.h);
      } else if (state === 'bought') {
        cg.fillStyle(GSKIN.fill.bought, 0.24);
        cg.fillRect(b.x + 6, b.y + 6, b.w - 12, b.h - 12);
      } else if (state === 'poor') {
        cg.fillStyle(0x000000, 0.20); cg.fillRect(b.x + 6, b.y + 6, b.w - 12, b.h - 12);
      }
      // 카드 몸과 독립된 이 선만 등급색을 바꾼다.
      cg.lineStyle(pointed ? Math.max(6, GSKIN.strokeW[state]) : GSKIN.strokeW[state], edge, 1);
      cg.strokeRect(b.x, b.y, b.w, b.h);
      const upPulse = up && !o.bought ? 0.5 + 0.5 * Math.sin(this.uiT * 8 + i * 0.8) : 0;
      if (up && !o.bought && !pointed) {
        cg.fillStyle(tierColor(upT), 0.10 + 0.09 * upPulse);
        cg.fillRect(b.x + 5, b.y + 5, b.w - 10, b.h - 10);
      }
      // **몸은 놓인 물건의 등급이다.** 오른 등급을 여기 쓰면 카드가 거짓이 된다.
      // ⓑ 바깥 한 겹 — 넷 중 하나만 다르게 보이면 눈이 먼저 간다.
      // ⓐ 모서리 배지 ▲ — 「여기는 강화된다」. **글자를 안 쓴다**(사장님: 표시 정도만).
      //   ⚠ 텍스트 객체가 아니라 **그래픽으로 그린다.** `g` 는 매 프레임 지워지므로
      //     칸이 바뀌었을 때 배지만 남는 사고가 구조적으로 안 난다(텍스트였으면
      //     `hideAll` 에 한 줄을 더 챙겨야 하고, 그 한 줄을 잊는 것이 이 저장소의
      //     상습 결함이다). 자리는 `[N]` 열쇠 왼쪽 — 어느 칸과도 안 겹친다.
      if (up && !o.bought) {
        if (!pointed) {
          cg.lineStyle(3 + Math.round(upPulse), tierColor(upT), 0.76 + 0.24 * upPulse);
          cg.strokeRoundedRect(b.x - 4, b.y - 4, b.w + 8, b.h + 8, GSKIN.r + 3);
        }
        const ax = b.x + b.w - 48, ay = b.y + 11, aw = 12, ah = 11;
        if (isDiamondTier(upT)) {
          drawDiamond(cg, ax + aw / 2, ay + ah / 2 + 1, 0.72 + upPulse * 0.12, 1);
        } else {
          cg.fillStyle(tierColor(upT), 1);
          cg.fillTriangle(ax, ay + ah, ax + aw, ay + ah, ax + aw / 2, ay);
          cg.fillStyle(tierColor(upT), 0.55);
          cg.fillRect(ax, ay + ah + 3, aw, 2);
        }
      }

      // 「따로 챙겨 둔다」 — 실제 보관 상태에만 기존 자물쇠 PNG를 붙인다.
      if (s.isLocked(i)) {
        const mark = GSKIN.keepLock;
        this.keepLocks[i].setPosition(b.x + b.w - mark.right, b.y + mark.y)
          .setDisplaySize(mark.size, mark.size).setAlpha(1).setVisible(true);
      }

      const ix = b.x + (b.w - GSKIN.icon.w) / 2;
      drawIconSlot(cg, ix, b.y + 34);
      placeIcon(this.scene, this.offIcon[i], ix, b.y + 34, o.part.slot, o.part.p.id);
      const cx = b.x + 14;
      let y = b.y + 34 + GSKIN.icon.h + 14;
      for (const [k, v] of Object.entries(t)) {
        if (k === 'rows') for (const r of v) { r.ko.setAlpha(1); r.val.setAlpha(1); }
        else v.setAlpha(1);
      }
      t.name.setAlpha(1);
      this.offIcon[i].setAlpha(1);
      t.key.setPosition(b.x + b.w - 14, b.y + 10).setText(`[${i + 1}]`)
        .setColor(pointed ? tierCss(o.part.t) : '#91b578').setVisible(true);
      t.slot.setPosition(cx, b.y + 12).setText(SLOT_KO[o.part.slot])
        .setColor(DIM).setVisible(true);
      t.name.setPosition(cx, y).setText(o.part.p.ko)
        .setColor(tierCss(o.part.t)).setVisible(true);
      y += 34;
      drawDots(cg, cx + 3, y + 6, o.part.t);
      // 낮은 급도 살 수 있다. 붉은 막대는 금지가 아니라 장착 뒤 헐거운 물림이다.
      drawWeight(cg, barX(b), y + 1, o.part.p.cls, !candidatePartLoose);
      y += 26;

      // ── 막대 두 줄 — **사면 얼마가 되는가.** 눈금이 지금 값이다 ──────
      const rows2 = BAR_ROWS[o.part.slot];
      const nx = barVals(after, statContext);
      for (let r = 0; r < 2; r++) {
        const [ko, ax] = rows2[r], v = nx[ax];
        drawBar(cg, barX(b), y + 2, MAX[ax] ? v / MAX[ax] : 0, tierColor(o.part.t),
                MAX[ax] ? nowV[ax] / MAX[ax] : 0);
        (r ? t.b1 : t.b0).setPosition(cx, y).setText(`${ko} ${v.toFixed(BAR_DP[ax])}`)
          .setColor(INK).setVisible(true);
        y += 22;
      }
      // 세트 진행과 돌 피해 전달은 같은 한 줄을 공유해 카드 기하를 늘리지 않는다.
      // 급 손실은 색만으로 말하지 않고 경고 기호와 정확한 계산 대상을 함께 적는다.
      // 비호버에서는 실제 주력 위력/초를 먼저, 전달률을 바로 아래에 둔다. 호버의 15행
      // 비교는 기존 시작점을 유지해 카드 하단 기하와 상세 정보량을 바꾸지 않는다.
      const sl = setLine(after);
      const support = c.grip?.value ?? combatSnapshot(after, statContext).support;
      const gripText = candidateLoose.size
        ? `⚠ 돌 피해 전달 ${Math.round(support * 100)}%`
        : support < 1 ? `돌 피해 전달 ${Math.round(support * 100)}%` : '';
      const statusY = pointed ? y : y + 22;
      t.set.setPosition(cx, statusY).setText(gripText || sl.txt || '')
        .setColor(candidateLoose.size ? BAD : sl.hot ? '#e5bd68' : DIM)
        .setVisible(!!(gripText || sl.txt));

      if (pointed) {
        y += 22;
        // ── 오버: 지금 대비 비교 ──
        // 숫자의 위·아래가 아니라 **성능의 이득·손해**를 화살표와 색이 함께 말한다.
        // 실제 숫자 방향은 `현재 → 장착 후`로 따로 보이므로 빨강 ▲·초록 ▼가 없다.
        t.per.setVisible(false);
        const pv = preview;
        for (let k = 0; k < t.rows.length; k++) {
          const r = t.rows[k], d = pv[k];
          if (!d) { r.ko.setVisible(false); r.val.setVisible(false); continue; }
          r.ko.setPosition(cx, y + k * CANDIDATE_DETAIL_ROW_GAP).setText(d.ko).setColor(DIM).setVisible(true);
          r.val.setPosition(b.x + b.w - 14, y + k * CANDIDATE_DETAIL_ROW_GAP)
            .setText(`${d.arrow} ${d.comparison ?? d.beforeAfter}`)
            .setColor(d.tone === 'good' ? GOOD : BAD).setVisible(true);
        }
      } else {
        // 평시에도 실제 비교 정본의 가장 중요한 변화 한 줄은 읽힌다. 호버하면 바로
        // 같은 자리의 첫 상세 행부터 기존 최대 15행 비교가 펼쳐진다.
        t.per.setPosition(cx, y).setText(lead?.text ?? c.per)
          .setColor(lead ? (lead.tone === 'good' ? GOOD : BAD) : DIM).setVisible(true);
        for (const r of t.rows) { r.ko.setVisible(false); r.val.setVisible(false); }
      }

      t.price.setOrigin(1, 0)
        .setPosition(b.x + b.w - 14, b.y + b.h - 32)
        // 산 뒤에도 **무슨 일이 일어났는지** 말한다 — 「바꿔 꼈다」와 「등급이 올랐다」는
        //   다른 사건이다. 산 칸의 카드는 다시 안 만들어지므로 `up` 이 그대로 남는다.
        .setText(o.bought ? (upT === TIER_DIAMOND ? '다이아로 올랐다' : up ? '등급이 올랐다' : '바꿔 꼈다')
          : `${price} 치즈`)
        .setColor(o.bought ? GOOD : s.wallet < price ? BAD : '#e5bd68')
        .setAlpha(1).setVisible(true);
    }

    // ── 리롤 ────────────────────────────────────────────────────
    const rx = W / 2 - 190, ry = H - 118;
    const canDraw = s.canRedraw(), can = s.canReroll();
    const pad = this.scene.currentGamepadLabels?.()
      ?? { confirm: 'A', cancel: 'B', lock: 'X', reroll: 'Y' };
    g.fillStyle(can ? 0x25292b : 0x151719, 1); g.fillRoundedRect(rx, ry, 380, 56, GSKIN.r);
    g.lineStyle(can && this.hoverReroll ? 3 : 2, can ? 0xa9926a : 0x44484a, 1);
    g.strokeRoundedRect(rx, ry, 380, 56, GSKIN.r);
    this.rerollTxt.setPosition(W / 2, ry + 17)
      .setText(!canDraw ? '리롤 불가 — 바뀔 자리가 없다' : `[R / ${pad.reroll}] 리롤  ${s.nextRerollPrice}치즈`)
      .setColor(can ? INK : '#747672').setVisible(true);

    const ruleButton = rulesButtonBox();
    g.fillStyle(0x25292b, 1);
    g.fillRoundedRect(ruleButton.x, ruleButton.y, ruleButton.w, ruleButton.h, GSKIN.r);
    g.lineStyle(2, 0xa9926a, 1);
    g.strokeRoundedRect(ruleButton.x, ruleButton.y, ruleButton.w, ruleButton.h, GSKIN.r);
    this.rulesButtonTxt.setPosition(ruleButton.x + ruleButton.w / 2, ruleButton.y + 10)
      .setText('[Tab] 규칙').setVisible(true);

    this.foot.setPosition(W / 2, H - 42)
      .setText(`치즈 ${s.wallet}  ·  1–4 / ${pad.confirm} 구매  ·  QWET / ${pad.lock} 챙기기  ·  Tab 규칙  ·  Space / ${pad.cancel} 닫기`)
      .setVisible(true);
    this.renderRules();
  }
}
